import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { sponsorAPI, institutionsAPI, uploadAPI } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  Megaphone, BarChart3, PlusCircle, LayoutGrid, DollarSign,
  Eye, MousePointerClick, TrendingUp, Pause, Play, Image,
  ExternalLink, Target, Calendar, Upload, X, ChevronDown,
  Activity, Percent, Wallet, FileText, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import usePageTitle from '../hooks/usePageTitle';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend
} from 'recharts';

const STATUS_COLORS = {
  draft: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  paused: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  ended: 'bg-red-500/20 text-red-300 border-red-500/30',
};

const PLACEMENT_TYPES = [
  { value: 'feed_sidebar', label: 'Feed Sidebar' },
  { value: 'in_feed', label: 'In-Feed Sponsored' },
  { value: 'banner', label: 'Banner' },
  { value: 'sponsored_listing', label: 'Sponsored Listing' },
];

const PLACEMENT_DESCRIPTIONS = {
  feed_sidebar: {
    title: 'Feed Sidebar',
    description: 'Displayed in the right sidebar of the main feed. Visible on desktop viewports alongside the user\'s scrolling content. Great for brand awareness with a persistent visual presence.',
    dimensions: '300x250px recommended',
    visibility: 'Desktop only, persistent while scrolling',
  },
  in_feed: {
    title: 'In-Feed Sponsored',
    description: 'Native-style card inserted directly into the user\'s feed between organic posts. Blends naturally with content for higher engagement rates.',
    dimensions: '600x400px recommended',
    visibility: 'All devices, appears every 5-8 posts',
  },
  banner: {
    title: 'Banner',
    description: 'Full-width banner displayed at the top of key pages (Dashboard, Events, Jobs). Maximum visibility for time-sensitive promotions and announcements.',
    dimensions: '728x90px recommended',
    visibility: 'All devices, top of page',
  },
  sponsored_listing: {
    title: 'Sponsored Listing',
    description: 'Promoted listing pinned to the top of Jobs, Marketplace, or Housing pages. Includes a "Sponsored" badge and priority placement above organic results.',
    dimensions: 'Standard listing format',
    visibility: 'All devices, top of listing pages',
  },
};

const SponsorPanel = () => {
  usePageTitle('Sponsor Dashboard');
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [selectedAnalyticsCampaign, setSelectedAnalyticsCampaign] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Create campaign form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    link_url: '',
    creative_image: '',
    placement_type: '',
    target_institutions: [],
    target_year_start: '',
    target_year_end: '',
    target_location: '',
    target_interests: [],
    budget: '',
    daily_budget: '',
    start_date: '',
    end_date: '',
  });
  const [interestInput, setInterestInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [institutionSearch, setInstitutionSearch] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [togglingCampaign, setTogglingCampaign] = useState(null);

  const isAuthorized = user?.roles?.includes('sponsor') ||
    user?.roles?.includes('advertiser') ||
    user?.roles?.includes('admin') ||
    user?.role === 'admin';

  useEffect(() => {
    if (!isAuthorized) {
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
    fetchInstitutions();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const res = await sponsorAPI.getDashboard();
      setDashboard(res.data);
      setCampaigns(res.data.campaigns || []);
    } catch (e) {
      toast.error('Failed to load sponsor dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const res = await institutionsAPI.list();
      setInstitutions(res.data || []);
    } catch (e) {
      // Institutions may not be available yet
    }
  };

  const fetchCampaignAnalytics = useCallback(async (campaignId) => {
    if (!campaignId) {
      setAnalyticsData(null);
      return;
    }
    setAnalyticsLoading(true);
    try {
      const res = await sponsorAPI.getCampaignAnalytics(campaignId);
      setAnalyticsData(res.data);
    } catch (e) {
      toast.error('Failed to load campaign analytics');
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadAPI.postImage(file);
      setForm(prev => ({ ...prev, creative_image: res.data.url }));
      toast.success('Image uploaded');
    } catch (e) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const addInterest = () => {
    const tag = interestInput.trim().toLowerCase();
    if (tag && !form.target_interests.includes(tag)) {
      setForm(prev => ({
        ...prev,
        target_interests: [...prev.target_interests, tag],
      }));
    }
    setInterestInput('');
  };

  const removeInterest = (tag) => {
    setForm(prev => ({
      ...prev,
      target_interests: prev.target_interests.filter(t => t !== tag),
    }));
  };

  const toggleInstitution = (inst) => {
    const id = inst._id || inst.id;
    setForm(prev => {
      const current = prev.target_institutions;
      if (current.includes(id)) {
        return { ...prev, target_institutions: current.filter(i => i !== id) };
      }
      return { ...prev, target_institutions: [...current, id] };
    });
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Campaign title is required');
      return;
    }
    if (!form.placement_type) {
      toast.error('Select a placement type');
      return;
    }
    if (!form.budget || parseFloat(form.budget) <= 0) {
      toast.error('Enter a valid budget');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        link_url: form.link_url.trim(),
        creative_image: form.creative_image,
        placement_type: form.placement_type,
        targeting: {
          institutions: form.target_institutions,
          year_start: form.target_year_start ? parseInt(form.target_year_start) : null,
          year_end: form.target_year_end ? parseInt(form.target_year_end) : null,
          location: form.target_location.trim() || null,
          interests: form.target_interests,
        },
        budget: parseFloat(form.budget),
        daily_budget: form.daily_budget ? parseFloat(form.daily_budget) : null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      await sponsorAPI.createCampaign(payload);
      toast.success('Campaign created successfully');
      setForm({
        title: '', description: '', link_url: '', creative_image: '',
        placement_type: '', target_institutions: [], target_year_start: '',
        target_year_end: '', target_location: '', target_interests: [],
        budget: '', daily_budget: '', start_date: '', end_date: '',
      });
      fetchDashboard();
      setActiveTab('dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const toggleCampaignStatus = async (campaign) => {
    const newStatus = campaign.status === 'active' ? 'paused' : 'active';
    setTogglingCampaign(campaign._id || campaign.id);
    try {
      await sponsorAPI.updateCampaign(campaign._id || campaign.id, { status: newStatus });
      toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchDashboard();
    } catch (e) {
      toast.error('Failed to update campaign status');
    } finally {
      setTogglingCampaign(null);
    }
  };

  const filteredInstitutions = institutions.filter(inst =>
    (inst.name || '').toLowerCase().includes(institutionSearch.toLowerCase())
  );

  const getInstitutionName = (id) => {
    const inst = institutions.find(i => (i._id || i.id) === id);
    return inst?.name || id;
  };

  const stats = dashboard?.stats || {};
  const totalImpressions = stats.total_impressions || 0;
  const totalClicks = stats.total_clicks || 0;
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
  const totalSpend = stats.total_spend || 0;
  const activeCampaigns = campaigns.filter(c => c.status === 'active').length;

  if (!isAuthorized) return null;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-600/20 rounded-xl border border-purple-500/30">
              <Megaphone className="w-7 h-7 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Sponsor Dashboard</h1>
              <p className="text-slate-400 text-sm">Manage campaigns, track performance, and reach your audience</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-800/50 border border-slate-700/50 mb-6 flex flex-wrap">
              <TabsTrigger value="dashboard" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Dashboard
              </TabsTrigger>
              <TabsTrigger value="create" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <PlusCircle className="w-4 h-4 mr-1.5" /> Create Campaign
              </TabsTrigger>
              <TabsTrigger value="placements" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <LayoutGrid className="w-4 h-4 mr-1.5" /> Ad Placements
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <TrendingUp className="w-4 h-4 mr-1.5" /> Analytics
              </TabsTrigger>
              <TabsTrigger value="billing" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <Wallet className="w-4 h-4 mr-1.5" /> Billing
              </TabsTrigger>
            </TabsList>

            {/* ==================== DASHBOARD TAB ==================== */}
            <TabsContent value="dashboard">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-400" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <Card className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-slate-400">Total Campaigns</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{campaigns.length}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity className="w-4 h-4 text-emerald-400" />
                          <span className="text-xs text-slate-400">Active</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-400">{activeCampaigns}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Eye className="w-4 h-4 text-blue-400" />
                          <span className="text-xs text-slate-400">Impressions</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{totalImpressions.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <MousePointerClick className="w-4 h-4 text-orange-400" />
                          <span className="text-xs text-slate-400">Clicks</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{totalClicks.toLocaleString()}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <Percent className="w-4 h-4 text-cyan-400" />
                          <span className="text-xs text-slate-400">CTR</span>
                        </div>
                        <p className="text-2xl font-bold text-cyan-400">{ctr}%</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-green-400" />
                          <span className="text-xs text-slate-400">Total Spend</span>
                        </div>
                        <p className="text-2xl font-bold text-green-400">${totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Campaign List */}
                  <Card className="bg-slate-800/60 border-slate-700/50">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Megaphone className="w-5 h-5 text-purple-400" />
                        Your Campaigns
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {campaigns.length === 0 ? (
                        <div className="text-center py-12">
                          <Megaphone className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                          <p className="text-slate-400 mb-4">No campaigns yet. Create your first campaign to start reaching alumni.</p>
                          <Button
                            onClick={() => setActiveTab('create')}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <PlusCircle className="w-4 h-4 mr-2" /> Create Campaign
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {campaigns.map(campaign => {
                            const cId = campaign._id || campaign.id;
                            const impressions = campaign.impressions || 0;
                            const clicks = campaign.clicks || 0;
                            const campaignCtr = impressions > 0 ? ((clicks / impressions) * 100).toFixed(2) : '0.00';
                            return (
                              <div
                                key={cId}
                                className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-4 bg-slate-900/50 rounded-lg border border-slate-700/30 hover:border-purple-500/30 transition-colors"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-white font-medium truncate">{campaign.title}</h3>
                                    <Badge className={`text-xs border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}>
                                      {campaign.status || 'draft'}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">
                                      {PLACEMENT_TYPES.find(p => p.value === campaign.placement_type)?.label || campaign.placement_type}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-slate-400 truncate">{campaign.description || 'No description'}</p>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <div className="text-center">
                                    <p className="text-slate-400 text-xs">Impressions</p>
                                    <p className="text-white font-medium">{impressions.toLocaleString()}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-slate-400 text-xs">Clicks</p>
                                    <p className="text-white font-medium">{clicks.toLocaleString()}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-slate-400 text-xs">CTR</p>
                                    <p className="text-cyan-400 font-medium">{campaignCtr}%</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-slate-400 text-xs">Spend</p>
                                    <p className="text-green-400 font-medium">${(campaign.spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* ==================== CREATE CAMPAIGN TAB ==================== */}
            <TabsContent value="create">
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <Card className="bg-slate-800/60 border-slate-700/50">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <PlusCircle className="w-5 h-5 text-purple-400" />
                        Create New Campaign
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleCreateCampaign} className="space-y-6">
                        {/* Basic Info */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Basic Information</h3>
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Campaign Title *</label>
                            <Input
                              value={form.title}
                              onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                              placeholder="e.g., Spring Recruitment Drive 2026"
                              className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Description</label>
                            <Textarea
                              value={form.description}
                              onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Describe your campaign goals and messaging..."
                              rows={3}
                              className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Link URL</label>
                            <Input
                              type="url"
                              value={form.link_url}
                              onChange={e => setForm(prev => ({ ...prev, link_url: e.target.value }))}
                              placeholder="https://your-landing-page.com"
                              className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                            />
                          </div>
                        </div>

                        {/* Creative */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Creative</h3>
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Ad Image</label>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                            {form.creative_image ? (
                              <div className="relative inline-block">
                                <img
                                  src={form.creative_image}
                                  alt="Ad creative"
                                  className="max-w-xs max-h-48 rounded-lg border border-slate-700"
                                />
                                <button
                                  type="button"
                                  onClick={() => setForm(prev => ({ ...prev, creative_image: '' }))}
                                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700"
                              >
                                {uploading ? (
                                  <span className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400" />
                                    Uploading...
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-2">
                                    <Upload className="w-4 h-4" /> Upload Image
                                  </span>
                                )}
                              </Button>
                            )}
                            <p className="text-xs text-slate-500 mt-1">Max 5MB. Recommended: 600x400px for in-feed, 300x250px for sidebar, 728x90px for banner.</p>
                          </div>
                        </div>

                        {/* Placement */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Placement</h3>
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Placement Type *</label>
                            <Select
                              value={form.placement_type}
                              onValueChange={val => setForm(prev => ({ ...prev, placement_type: val }))}
                            >
                              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                                <SelectValue placeholder="Select placement type" />
                              </SelectTrigger>
                              <SelectContent>
                                {PLACEMENT_TYPES.map(pt => (
                                  <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Targeting */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Targeting</h3>

                          {/* Institution Multi-Select */}
                          <div className="relative">
                            <label className="block text-sm text-slate-300 mb-1">Target Institutions</label>
                            <div
                              className="min-h-[42px] bg-slate-900/50 border border-slate-700 rounded-md p-2 cursor-pointer flex flex-wrap gap-1.5"
                              onClick={() => setShowInstitutionDropdown(!showInstitutionDropdown)}
                            >
                              {form.target_institutions.length === 0 ? (
                                <span className="text-slate-500 text-sm py-0.5">All institutions (click to filter)</span>
                              ) : (
                                form.target_institutions.map(id => (
                                  <Badge
                                    key={id}
                                    className="bg-purple-600/30 text-purple-300 border-purple-500/30 text-xs flex items-center gap-1"
                                  >
                                    {getInstitutionName(id)}
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        setForm(prev => ({
                                          ...prev,
                                          target_institutions: prev.target_institutions.filter(i => i !== id),
                                        }));
                                      }}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))
                              )}
                              <ChevronDown className="w-4 h-4 text-slate-400 ml-auto self-center" />
                            </div>
                            {showInstitutionDropdown && (
                              <div className="absolute z-20 w-full mt-1 bg-slate-800 border border-slate-700 rounded-md shadow-xl max-h-48 overflow-y-auto">
                                <div className="p-2 sticky top-0 bg-slate-800">
                                  <Input
                                    value={institutionSearch}
                                    onChange={e => setInstitutionSearch(e.target.value)}
                                    placeholder="Search institutions..."
                                    className="bg-slate-900/50 border-slate-700 text-white text-sm h-8"
                                    onClick={e => e.stopPropagation()}
                                  />
                                </div>
                                {filteredInstitutions.map(inst => {
                                  const instId = inst._id || inst.id;
                                  const selected = form.target_institutions.includes(instId);
                                  return (
                                    <button
                                      type="button"
                                      key={instId}
                                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700 flex items-center gap-2 ${selected ? 'text-purple-300 bg-purple-600/10' : 'text-slate-300'}`}
                                      onClick={e => {
                                        e.stopPropagation();
                                        toggleInstitution(inst);
                                      }}
                                    >
                                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${selected ? 'bg-purple-600 border-purple-600' : 'border-slate-600'}`}>
                                        {selected && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                      {inst.name}
                                    </button>
                                  );
                                })}
                                {filteredInstitutions.length === 0 && (
                                  <p className="px-3 py-2 text-sm text-slate-500">No institutions found</p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Year Range */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-slate-300 mb-1">Graduation Year From</label>
                              <Input
                                type="number"
                                min="1900"
                                max="2100"
                                value={form.target_year_start}
                                onChange={e => setForm(prev => ({ ...prev, target_year_start: e.target.value }))}
                                placeholder="e.g., 2020"
                                className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-slate-300 mb-1">Graduation Year To</label>
                              <Input
                                type="number"
                                min="1900"
                                max="2100"
                                value={form.target_year_end}
                                onChange={e => setForm(prev => ({ ...prev, target_year_end: e.target.value }))}
                                placeholder="e.g., 2026"
                                className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                              />
                            </div>
                          </div>

                          {/* Location */}
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Target Location</label>
                            <Input
                              value={form.target_location}
                              onChange={e => setForm(prev => ({ ...prev, target_location: e.target.value }))}
                              placeholder="e.g., Atlanta, GA or leave blank for all"
                              className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                            />
                          </div>

                          {/* Interests */}
                          <div>
                            <label className="block text-sm text-slate-300 mb-1">Interest Tags</label>
                            <div className="flex gap-2">
                              <Input
                                value={interestInput}
                                onChange={e => setInterestInput(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addInterest();
                                  }
                                }}
                                placeholder="Type an interest and press Enter"
                                className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={addInterest}
                                className="border-slate-600 text-slate-300 hover:bg-slate-700 shrink-0"
                              >
                                Add
                              </Button>
                            </div>
                            {form.target_interests.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {form.target_interests.map(tag => (
                                  <Badge
                                    key={tag}
                                    className="bg-slate-700/50 text-slate-300 border-slate-600 text-xs flex items-center gap-1"
                                  >
                                    {tag}
                                    <button type="button" onClick={() => removeInterest(tag)}>
                                      <X className="w-3 h-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Budget & Schedule */}
                        <div className="space-y-4">
                          <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider">Budget & Schedule</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-slate-300 mb-1">Total Budget ($) *</label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.budget}
                                onChange={e => setForm(prev => ({ ...prev, budget: e.target.value }))}
                                placeholder="500.00"
                                className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-slate-300 mb-1">Daily Budget ($)</label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.daily_budget}
                                onChange={e => setForm(prev => ({ ...prev, daily_budget: e.target.value }))}
                                placeholder="25.00"
                                className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-slate-300 mb-1">Start Date</label>
                              <Input
                                type="date"
                                value={form.start_date}
                                onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                                className="bg-slate-900/50 border-slate-700 text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-slate-300 mb-1">End Date</label>
                              <Input
                                type="date"
                                value={form.end_date}
                                onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                                className="bg-slate-900/50 border-slate-700 text-white"
                              />
                            </div>
                          </div>
                        </div>

                        <Button
                          type="submit"
                          disabled={creating}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2.5"
                        >
                          {creating ? (
                            <span className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                              Creating Campaign...
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <PlusCircle className="w-4 h-4" /> Create Campaign
                            </span>
                          )}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>

                {/* Preview Panel */}
                <div>
                  <Card className="bg-slate-800/60 border-slate-700/50 sticky top-6">
                    <CardHeader>
                      <CardTitle className="text-white text-sm flex items-center gap-2">
                        <Eye className="w-4 h-4 text-purple-400" />
                        Ad Preview
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-slate-900/80 rounded-lg border border-slate-700/50 overflow-hidden">
                        {form.creative_image ? (
                          <img
                            src={form.creative_image}
                            alt="Ad preview"
                            className="w-full h-40 object-cover"
                          />
                        ) : (
                          <div className="w-full h-40 bg-slate-800 flex items-center justify-center">
                            <Image className="w-8 h-8 text-slate-600" />
                          </div>
                        )}
                        <div className="p-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Badge className="bg-purple-600/30 text-purple-300 border-purple-500/30 text-[10px]">Sponsored</Badge>
                            {form.placement_type && (
                              <span className="text-[10px] text-slate-500">
                                {PLACEMENT_TYPES.find(p => p.value === form.placement_type)?.label}
                              </span>
                            )}
                          </div>
                          <h4 className="text-white text-sm font-medium truncate">
                            {form.title || 'Campaign Title'}
                          </h4>
                          <p className="text-slate-400 text-xs mt-0.5 line-clamp-2">
                            {form.description || 'Your campaign description will appear here...'}
                          </p>
                          {form.link_url && (
                            <div className="flex items-center gap-1 mt-2 text-purple-400 text-xs">
                              <ExternalLink className="w-3 h-3" />
                              <span className="truncate">{form.link_url}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {form.target_institutions.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-500 mb-1">Targeting {form.target_institutions.length} institution(s)</p>
                        </div>
                      )}
                      {(form.target_year_start || form.target_year_end) && (
                        <p className="text-xs text-slate-500 mt-1">
                          Grad years: {form.target_year_start || '...'} - {form.target_year_end || '...'}
                        </p>
                      )}
                      {form.target_location && (
                        <p className="text-xs text-slate-500 mt-1">Location: {form.target_location}</p>
                      )}
                      {form.budget && (
                        <p className="text-xs text-slate-500 mt-1">Budget: ${parseFloat(form.budget || 0).toFixed(2)}{form.daily_budget ? ` ($${parseFloat(form.daily_budget).toFixed(2)}/day)` : ''}</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* ==================== AD PLACEMENTS TAB ==================== */}
            <TabsContent value="placements">
              <div className="space-y-6">
                {/* Placement Type Explanations */}
                <div className="grid md:grid-cols-2 gap-4">
                  {Object.entries(PLACEMENT_DESCRIPTIONS).map(([key, info]) => (
                    <Card key={key} className="bg-slate-800/60 border-slate-700/50">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-purple-600/20 rounded-lg border border-purple-500/30 shrink-0">
                            {key === 'feed_sidebar' && <LayoutGrid className="w-5 h-5 text-purple-400" />}
                            {key === 'in_feed' && <FileText className="w-5 h-5 text-purple-400" />}
                            {key === 'banner' && <Megaphone className="w-5 h-5 text-purple-400" />}
                            {key === 'sponsored_listing' && <Target className="w-5 h-5 text-purple-400" />}
                          </div>
                          <div>
                            <h3 className="text-white font-medium mb-1">{info.title}</h3>
                            <p className="text-slate-400 text-sm mb-2">{info.description}</p>
                            <div className="flex flex-wrap gap-2">
                              <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">
                                {info.dimensions}
                              </Badge>
                              <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">
                                {info.visibility}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Active Campaigns by Placement */}
                <Card className="bg-slate-800/60 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="text-white">Campaigns by Placement</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {campaigns.length === 0 ? (
                      <p className="text-slate-400 text-center py-8">No campaigns to display.</p>
                    ) : (
                      <div className="space-y-6">
                        {PLACEMENT_TYPES.map(pt => {
                          const placementCampaigns = campaigns.filter(c => c.placement_type === pt.value);
                          if (placementCampaigns.length === 0) return null;
                          return (
                            <div key={pt.value}>
                              <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-3">{pt.label}</h3>
                              <div className="space-y-2">
                                {placementCampaigns.map(campaign => {
                                  const cId = campaign._id || campaign.id;
                                  const canToggle = campaign.status === 'active' || campaign.status === 'paused';
                                  return (
                                    <div
                                      key={cId}
                                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700/30"
                                    >
                                      <div className="flex items-center gap-3 min-w-0">
                                        <Badge className={`text-xs border shrink-0 ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}>
                                          {campaign.status}
                                        </Badge>
                                        <span className="text-white text-sm truncate">{campaign.title}</span>
                                      </div>
                                      {canToggle && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => toggleCampaignStatus(campaign)}
                                          disabled={togglingCampaign === cId}
                                          className={`shrink-0 text-xs ${
                                            campaign.status === 'active'
                                              ? 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10'
                                              : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'
                                          }`}
                                        >
                                          {togglingCampaign === cId ? (
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" />
                                          ) : campaign.status === 'active' ? (
                                            <span className="flex items-center gap-1"><Pause className="w-3 h-3" /> Pause</span>
                                          ) : (
                                            <span className="flex items-center gap-1"><Play className="w-3 h-3" /> Activate</span>
                                          )}
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                        {/* Show campaigns without a recognized placement */}
                        {campaigns.filter(c => !PLACEMENT_TYPES.some(pt => pt.value === c.placement_type)).length > 0 && (
                          <div>
                            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Other</h3>
                            <div className="space-y-2">
                              {campaigns
                                .filter(c => !PLACEMENT_TYPES.some(pt => pt.value === c.placement_type))
                                .map(campaign => {
                                  const cId = campaign._id || campaign.id;
                                  return (
                                    <div
                                      key={cId}
                                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700/30"
                                    >
                                      <div className="flex items-center gap-3 min-w-0">
                                        <Badge className={`text-xs border shrink-0 ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}>
                                          {campaign.status}
                                        </Badge>
                                        <span className="text-white text-sm truncate">{campaign.title}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* ==================== ANALYTICS TAB ==================== */}
            <TabsContent value="analytics">
              <div className="space-y-6">
                {/* Campaign Selector */}
                <Card className="bg-slate-800/60 border-slate-700/50">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <label className="text-sm text-slate-300 shrink-0">Select Campaign:</label>
                      <Select
                        value={selectedAnalyticsCampaign}
                        onValueChange={val => {
                          setSelectedAnalyticsCampaign(val);
                          fetchCampaignAnalytics(val);
                        }}
                      >
                        <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white md:max-w-sm">
                          <SelectValue placeholder="Choose a campaign to view analytics" />
                        </SelectTrigger>
                        <SelectContent>
                          {campaigns.map(c => (
                            <SelectItem key={c._id || c.id} value={c._id || c.id}>
                              {c.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {analyticsLoading && (
                  <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-400" />
                  </div>
                )}

                {!analyticsLoading && selectedAnalyticsCampaign && analyticsData && (
                  <div className="space-y-6">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-slate-800/60 border-slate-700/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Eye className="w-4 h-4 text-blue-400" />
                            <span className="text-xs text-slate-400">Impressions</span>
                          </div>
                          <p className="text-2xl font-bold text-white">{(analyticsData.total_impressions || 0).toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800/60 border-slate-700/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <MousePointerClick className="w-4 h-4 text-orange-400" />
                            <span className="text-xs text-slate-400">Clicks</span>
                          </div>
                          <p className="text-2xl font-bold text-white">{(analyticsData.total_clicks || 0).toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800/60 border-slate-700/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Percent className="w-4 h-4 text-cyan-400" />
                            <span className="text-xs text-slate-400">Overall CTR</span>
                          </div>
                          <p className="text-2xl font-bold text-cyan-400">
                            {analyticsData.total_impressions > 0
                              ? ((analyticsData.total_clicks / analyticsData.total_impressions) * 100).toFixed(2)
                              : '0.00'}%
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-slate-800/60 border-slate-700/50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="w-4 h-4 text-green-400" />
                            <span className="text-xs text-slate-400">Spend</span>
                          </div>
                          <p className="text-2xl font-bold text-green-400">
                            ${(analyticsData.total_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Daily Chart */}
                    {analyticsData.daily && analyticsData.daily.length > 0 && (
                      <Card className="bg-slate-800/60 border-slate-700/50">
                        <CardHeader>
                          <CardTitle className="text-white text-sm">Daily Impressions & Clicks</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={analyticsData.daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis
                                  dataKey="date"
                                  stroke="#64748b"
                                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                                  tickFormatter={val => {
                                    try { return format(parseISO(val), 'MMM d'); } catch { return val; }
                                  }}
                                />
                                <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                <Tooltip
                                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                                  labelStyle={{ color: '#e2e8f0' }}
                                  itemStyle={{ color: '#e2e8f0' }}
                                  labelFormatter={val => {
                                    try { return format(parseISO(val), 'MMM d, yyyy'); } catch { return val; }
                                  }}
                                />
                                <Legend />
                                <Line type="monotone" dataKey="impressions" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Impressions" />
                                <Line type="monotone" dataKey="clicks" stroke="#f97316" strokeWidth={2} dot={false} name="Clicks" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* CTR Over Time */}
                    {analyticsData.daily && analyticsData.daily.length > 0 && (
                      <Card className="bg-slate-800/60 border-slate-700/50">
                        <CardHeader>
                          <CardTitle className="text-white text-sm">CTR Over Time</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={analyticsData.daily.map(d => ({
                                  ...d,
                                  ctr: d.impressions > 0 ? parseFloat(((d.clicks / d.impressions) * 100).toFixed(2)) : 0,
                                }))}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis
                                  dataKey="date"
                                  stroke="#64748b"
                                  tick={{ fill: '#94a3b8', fontSize: 12 }}
                                  tickFormatter={val => {
                                    try { return format(parseISO(val), 'MMM d'); } catch { return val; }
                                  }}
                                />
                                <YAxis stroke="#64748b" tick={{ fill: '#94a3b8', fontSize: 12 }} unit="%" />
                                <Tooltip
                                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px' }}
                                  labelStyle={{ color: '#e2e8f0' }}
                                  formatter={(value) => [`${value}%`, 'CTR']}
                                  labelFormatter={val => {
                                    try { return format(parseISO(val), 'MMM d, yyyy'); } catch { return val; }
                                  }}
                                />
                                <Line type="monotone" dataKey="ctr" stroke="#06b6d4" strokeWidth={2} dot={false} name="CTR %" />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {!analyticsLoading && !selectedAnalyticsCampaign && (
                  <div className="text-center py-16">
                    <TrendingUp className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-400">Select a campaign above to view its analytics.</p>
                  </div>
                )}

                {/* Campaign Comparison Table */}
                {campaigns.length > 0 && (
                  <Card className="bg-slate-800/60 border-slate-700/50">
                    <CardHeader>
                      <CardTitle className="text-white text-sm">Campaign Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-700">
                              <th className="text-left text-slate-400 font-medium py-2 pr-4">Campaign</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Status</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Impressions</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Clicks</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">CTR</th>
                              <th className="text-right text-slate-400 font-medium py-2 pl-3">Spend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaigns.map(c => {
                              const cImpr = c.impressions || 0;
                              const cClk = c.clicks || 0;
                              const cCtr = cImpr > 0 ? ((cClk / cImpr) * 100).toFixed(2) : '0.00';
                              return (
                                <tr key={c._id || c.id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                                  <td className="py-2.5 pr-4 text-white">{c.title}</td>
                                  <td className="py-2.5 px-3 text-right">
                                    <Badge className={`text-xs border ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                                      {c.status}
                                    </Badge>
                                  </td>
                                  <td className="py-2.5 px-3 text-right text-slate-300">{cImpr.toLocaleString()}</td>
                                  <td className="py-2.5 px-3 text-right text-slate-300">{cClk.toLocaleString()}</td>
                                  <td className="py-2.5 px-3 text-right text-cyan-400">{cCtr}%</td>
                                  <td className="py-2.5 pl-3 text-right text-green-400">${(c.spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* ==================== BILLING TAB ==================== */}
            <TabsContent value="billing">
              <div className="space-y-6">
                {/* Total Spend Summary */}
                <Card className="bg-gradient-to-r from-purple-900/40 to-slate-800/60 border-purple-700/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-300 mb-1">Total Spend Across All Campaigns</p>
                        <p className="text-4xl font-bold text-white">
                          ${totalSpend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">Billing managed via Stripe subscription</p>
                      </div>
                      <div className="p-4 bg-purple-600/20 rounded-xl border border-purple-500/30">
                        <DollarSign className="w-10 h-10 text-purple-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Budget Tracking Table */}
                <Card className="bg-slate-800/60 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-purple-400" />
                      Budget Tracking
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {campaigns.length === 0 ? (
                      <p className="text-slate-400 text-center py-8">No campaigns to display budget information.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-700">
                              <th className="text-left text-slate-400 font-medium py-2 pr-4">Campaign</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Status</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Total Budget</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Spent</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Remaining</th>
                              <th className="text-right text-slate-400 font-medium py-2 px-3">Daily Budget</th>
                              <th className="text-right text-slate-400 font-medium py-2 pl-3">Usage</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaigns.map(c => {
                              const budget = c.budget || 0;
                              const spent = c.spend || 0;
                              const remaining = Math.max(0, budget - spent);
                              const usagePct = budget > 0 ? Math.min(100, (spent / budget) * 100).toFixed(1) : 0;
                              return (
                                <tr key={c._id || c.id} className="border-b border-slate-700/50 hover:bg-slate-700/20">
                                  <td className="py-3 pr-4 text-white font-medium">{c.title}</td>
                                  <td className="py-3 px-3 text-right">
                                    <Badge className={`text-xs border ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                                      {c.status}
                                    </Badge>
                                  </td>
                                  <td className="py-3 px-3 text-right text-slate-300">
                                    ${budget.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-3 px-3 text-right text-orange-400">
                                    ${spent.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-3 px-3 text-right text-emerald-400">
                                    ${remaining.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                  </td>
                                  <td className="py-3 px-3 text-right text-slate-300">
                                    {c.daily_budget
                                      ? `$${parseFloat(c.daily_budget).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                      : '-'}
                                  </td>
                                  <td className="py-3 pl-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                          className={`h-full rounded-full transition-all ${
                                            parseFloat(usagePct) > 90
                                              ? 'bg-red-500'
                                              : parseFloat(usagePct) > 70
                                              ? 'bg-yellow-500'
                                              : 'bg-purple-500'
                                          }`}
                                          style={{ width: `${usagePct}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-slate-400 w-12 text-right">{usagePct}%</span>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="border-t-2 border-slate-600">
                              <td className="py-3 pr-4 text-white font-semibold">Totals</td>
                              <td className="py-3 px-3" />
                              <td className="py-3 px-3 text-right text-white font-semibold">
                                ${campaigns.reduce((sum, c) => sum + (c.budget || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-3 px-3 text-right text-orange-400 font-semibold">
                                ${campaigns.reduce((sum, c) => sum + (c.spend || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-3 px-3 text-right text-emerald-400 font-semibold">
                                ${campaigns.reduce((sum, c) => sum + Math.max(0, (c.budget || 0) - (c.spend || 0)), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                              </td>
                              <td className="py-3 px-3" />
                              <td className="py-3 pl-3" />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default SponsorPanel;
